import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UserFavoriteWithArche } from '@/lib/api';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { archePath } from '@/lib/arche-url';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useNotificationSummary } from '@/hooks/useNotificationSummary';
import { useLongPress } from '../hooks/useLongPress';

interface FavoriteCardProps {
  favorite: UserFavoriteWithArche;
  /** Rearrange mode: the card becomes draggable and stops navigating on click. */
  arranging: boolean;
  /** Press-and-hold on the icon — the gesture that opens rearrange mode. */
  onLongPress: () => void;
}

export function FavoriteCard({ favorite, arranging, onLongPress }: FavoriteCardProps) {
  const navigate = useNavigate();
  const { badges } = useNotificationSummary();
  const badge = badges[favorite.archId];
  // Sorting is DISABLED outside rearrange mode, so no drag listeners are bound
  // and a slightly-dragged click can never displace an icon — the "I sometimes
  // accidentally move an icon" complaint. In rearrange mode the listeners come
  // back and the card is fully draggable.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: favorite.archId,
    disabled: !arranging,
  });
  const longPress = useLongPress(onLongPress, !arranging);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      {...longPress.handlers}
      className={`group relative flex flex-col items-center touch-none ${
        arranging ? 'cursor-grab' : 'cursor-pointer'
      }`}
      onClick={() => {
        // A long press opened rearrange mode; the click that ends it must not
        // also navigate. Same for any click made while arranging.
        if (longPress.firedRef.current) {
          longPress.firedRef.current = false;
          return;
        }
        if (arranging || isDragging) return;
        navigate(`/a/${archePath({ archId: favorite.archId, slug: favorite.archeSlug })}`);
      }}
    >
      {/* The wobble lives on an INNER wrapper: a CSS animation outranks an inline
          style, so animating the sortable node's own `transform` would fight
          dnd-kit's drag/shift transforms. `animationDelay` desynchronises the
          icons so the grid doesn't pulse in lockstep. */}
      <div
        className={`flex w-full flex-col items-center ${
          arranging ? 'worktop-jiggle animate-worktop-jiggle' : ''
        }`}
        style={
          arranging ? { animationDelay: `${(hashDelay(favorite.archId) % 12) * 20}ms` } : undefined
        }
      >
        {/* Square icon tile with notification badge overlay */}
        <div className="relative w-full">
          <FavoriteIcon favorite={favorite} />
          <NotificationBadge
            count={badge?.count ?? 0}
            showMention={(badge?.mentionCount ?? 0) > 0}
            className="absolute -top-1.5 -right-1.5 z-10 ring-2 ring-background shadow"
          />
        </div>

        {/* Label underneath */}
        <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
          {favorite.archeName}
        </span>
      </div>
    </div>
  );
}

/** Cheap, stable per-icon offset so the wobble phases differ across the grid. */
function hashDelay(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Reusable icon visual — also used in DragOverlay */
export function FavoriteIcon({ favorite }: { favorite: UserFavoriteWithArche }) {
  return favorite.archeImageRef ? (
    <ArcheIconImg
      imageRef={favorite.archeImageRef}
      size={128}
      alt={favorite.archeName}
      className="w-full aspect-square rounded-2xl object-cover shadow-md"
    />
  ) : (
    <DefaultArcheIcon className="w-full aspect-square rounded-2xl shadow-md" />
  );
}
